<template>
  <div class="w-full h-full">
    <Drawer direction="right" :open="showCreateSupportTicket" @update:open="(o) => !o && (showCreateSupportTicket = false)">
      <DrawerContent class="sm:max-w-md">
        <DrawerHeader class="text-left">
          <DrawerTitle>Create support ticket</DrawerTitle>
          <DrawerDescription>Create a support ticket on behalf of an end user</DrawerDescription>
        </DrawerHeader>
        <div class="flex flex-col gap-3 overflow-y-auto px-4 flex-1">
          <div class="grid gap-1.5">
            <label class="text-sm font-medium" for="tsr-end-user-id">End user Id</label>
            <Input id="tsr-end-user-id" v-model="transactionSupportRequest.endUserId" />
          </div>
          <div class="grid gap-1.5">
            <label class="text-sm font-medium" for="tsr-transaction-id">Transaction Id</label>
            <Input id="tsr-transaction-id" v-model="transactionSupportRequest.transactionId" />
          </div>
          <div class="grid gap-1.5">
            <label class="text-sm font-medium" for="tsr-program-offer-id">Program offer Id</label>
            <Input id="tsr-program-offer-id" v-model="transactionSupportRequest.programOfferId" />
          </div>
          <div class="grid gap-1.5">
            <label class="text-sm font-medium" for="tsr-end-user-message">Additional information</label>
            <Textarea id="tsr-end-user-message" v-model="transactionSupportRequest.endUserMessage" rows="4" class="min-h-25" />
          </div>
          <p v-if="createError" class="text-sm text-destructive">{{ createError }}</p>
        </div>
        <DrawerFooter class="flex-row justify-end gap-2">
          <Button variant="outline" @click="showCreateSupportTicket = false">Cancel</Button>
          <Button @click="generateTicket()">Submit ticket</Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>

    <Card>
      <CardHeader>
        <CardTitle>Support tickets</CardTitle>
        <CardDescription>View support tickets from your end users</CardDescription>
        <CardAction>
          <Button id="createTicketButton" size="sm" @click="showCreateSupportTicket = true">
            <Plus class="w-4 h-4 mr-1" />Create ticket
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent>
        <DataTable
          :headers="headersTable1"
          :items="retailers"
          :loading="loading"
          :error="error"
          @retry="refetch(serverOptions.page - 1)"
          v-model:server-options="serverOptions"
          :server-items-length="serverItemsLength"
          searchable
          search-placeholder="Search by ticket, user or offer id"
          :search="searchTerm"
          @update:search="searchTerm = $event"
          @search="onSearch"
          @clear-search="onClearSearch"
          :filter-groups="filterGroups"
          :filters="filters"
          @filter-change="onFilterChange"
        >
          <template #item-id="ticket">
            <CopyId :value="ticket.id" :max-width="120" />
          </template>
          <template #item-resolved="{ resolved }">
            <Badge v-if="resolved" variant="success">Resolved</Badge>
            <Badge v-else variant="warning">Open</Badge>
          </template>
          <template #item-endUserId="ticket">
            <CopyId :value="ticket.endUserId" :max-width="120" />
          </template>
          <template #item-expectedOfferId="ticket">
            <CopyId
              v-if="ticket.programOfferRequested && ticket.programOfferRequested.id"
              :value="ticket.programOfferRequested.id"
              :max-width="140"
            />
            <span v-else class="text-muted-foreground">—</span>
          </template>
          <template #item-createdOn="ticket">
            <span class="text-sm text-muted-foreground whitespace-nowrap">{{ niceDateTime(ticket.createdOn) }}</span>
          </template>
          <template #item-updatedOn="ticket">
            <span v-if="ticket.updatedOn != null" class="text-sm text-muted-foreground whitespace-nowrap">{{ niceDateTime(ticket.updatedOn) }}</span>
            <span v-else class="text-muted-foreground">—</span>
          </template>
          <template #item-action="ticket">
            <div class="flex justify-end items-center gap-1">
              <!-- Navigation, not an action: outline (light grey) rather than the dark primary. -->
              <Button variant="outline" size="sm" @click="$router.push(`/support/${ticket.id}`)">View ticket</Button>
            </div>
          </template>
        </DataTable>
      </CardContent>
    </Card>
  </div>
</template>

<script setup></script>

<script>
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardAction,
  Button,
  Badge,
  CopyId,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
  Input,
  Textarea,
} from '@boogi/ui'
import { Plus } from 'lucide-vue-next'
import { DateTime } from 'luxon'
import { DataTable } from '@boogi/ui'

export default {
  name: 'support-tickets',
  components: {
    Card,
    CardHeader,
    CardTitle,
    CardDescription,
    CardContent,
    CardAction,
    Button,
    Badge,
    CopyId,
    Drawer,
    DrawerContent,
    DrawerHeader,
    DrawerTitle,
    DrawerDescription,
    DrawerFooter,
    Input,
    Textarea,
    Plus,
    DataTable,
  },
  props: {},

  data() {
    return {
      serverOptions: {
        page: 1,
        rowsPerPage: 25,
      },
      serverItemsLength: undefined,
      showCreateSupportTicket: false,

      searchTerm: '',

      transactionSupportRequest: {
        endUserId: '',
        transactionId: '',
        programOfferId: '',
        endUserMessage: '',
      },
      createError: '',

      loading: true,
      error: false,
      headersTable1: [
        { text: 'Status', value: 'resolved' },
        { text: 'Ticket id', value: 'id' },
        { text: 'End user id', value: 'endUserId' },
        { text: 'Transaction description', value: 'transactionDescription' },
        // Simple key (not the dotted path) so the cell can use an #item- slot for CopyId.
        { text: 'Expected offer id', value: 'expectedOfferId' },
        { text: 'Created on', value: 'createdOn' },
        { text: 'Updated on', value: 'updatedOn' },
        { text: 'Action', value: 'action' },
      ],
      retailers: [],

      filterGroups: [
        {
          key: 'supportLevel',
          label: 'Support level',
          options: [
            { value: 'CLIENT', label: 'Client' },
            { value: 'BOOGI', label: 'Boogi' },
            { value: 'SUPPLIER', label: 'Supplier' },
          ],
        },
        {
          key: 'status',
          label: 'Status',
          options: [{ value: 'resolved', label: 'Resolved tickets' }],
        },
        {
          key: 'sort',
          label: 'Sort by',
          options: [{ value: 'oldest', label: 'Oldest first' }],
        },
      ],

      // Active filter state (source of truth for the query params).
      offerType: [{ id: 'CLIENT' }],
      reslovedBy: false,
      orderBy: 'LATEST',
    }
  },
  created() {
    this.getSupportTickets(0, 25)
  },
  computed: {
    filters() {
      return {
        supportLevel: this.offerType.map((o) => o.id),
        status: this.reslovedBy ? ['resolved'] : [],
        sort: this.orderBy === 'OLDEST' ? ['oldest'] : [],
      }
    },
  },
  methods: {
    niceDateTime(dateObject) {
      return DateTime.fromMillis(dateObject).toLocaleString(DateTime.DATETIME_MED)
    },

    selectedSupportLevels() {
      return this.offerType.map((element) => element.id)
    },

    onFilterChange(updated) {
      this.offerType = (updated.supportLevel || []).map((id) => ({ id }))
      this.reslovedBy = (updated.status || []).includes('resolved')
      this.orderBy = (updated.sort || []).includes('oldest') ? 'OLDEST' : 'LATEST'
      this.serverOptions = { page: 1, rowsPerPage: this.serverOptions.rowsPerPage || 25 }
    },

    onSearch() {
      this.serverOptions = { page: 1, rowsPerPage: this.serverOptions.rowsPerPage || 25 }
    },

    onClearSearch() {
      this.searchTerm = ''
      this.serverOptions = { page: 1, rowsPerPage: this.serverOptions.rowsPerPage || 25 }
    },

    refetch(page) {
      const size = this.serverOptions.rowsPerPage || 25
      this.getSupportTickets(page, size)
    },

    async fetchTickets(url) {
      const token = localStorage.getItem('token')
      this.loading = true
      this.error = false
      try {
        const response = await this.$apiFetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `${token}`,
          },
        })

        if (response.ok) {
          const object = await response.json()
          this.serverItemsLength = object.totalElements
          this.retailers = object.content
        } else {
          this.error = true
        }
      } catch (error) {
        this.error = true
      } finally {
        this.loading = false
      }
    },

    async getSupportTickets(page, size) {
      const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL
      const supportLevels = this.selectedSupportLevels()
      const searchParam = this.searchTerm ? '&search=' + encodeURIComponent(this.searchTerm) : ''
      const url = `${apiUrl}/private/api/client/support?page=${page}&size=${size}&sort=${this.orderBy}&resolved=${this.reslovedBy}${supportLevels.length > 0 ? '&supportLevel=' + supportLevels : ''}${searchParam}`
      await this.fetchTickets(url)
    },

    async generateTicket() {
      this.createError = ''
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        this.loading = true
        const response = await this.$apiFetch(`${apiUrl}/private/api/client/support`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `${token}`,
          },
          body: JSON.stringify(this.transactionSupportRequest),
        })

        if (response.ok) {
          this.getSupportTickets(0, 25)
          this.showCreateSupportTicket = false
        } else {
          // A ticket needs a real transaction id AND a program offer id that
          // belong to this publisher — surface that instead of failing silently.
          const text = await response.text().catch(() => '')
          this.createError =
            text ||
            'Could not create the ticket. Check the Transaction Id and Program offer Id exist for your account.'
        }
      } catch (error) {
        this.createError = 'Could not create the ticket. Please try again.'
      } finally {
        this.loading = false
      }
    },
  },
  mounted() {
    document.title = 'Dashboard | Support tickets'
  },
  watch: {
    serverOptions: {
      deep: true,
      handler(val) {
        this.refetch(val.page - 1)
      },
    },
  },
}
</script>
